import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import styled from '@emotion/styled';
import {
  fontSize,
  fontWeight,
  spacing,
  mediaMax,
  zIndex,
  mediaMin,
  borderRadius,
} from 'presentations/views/styles';
import {
  black,
  gray01,
  gray04,
  primary,
  white,
} from 'presentations/views/styles/color';
import ResourceContents from 'presentations/views/components/organisms/ResourceContents';
import SubListItem from 'presentations/views/components/molecules/ResourceSubListItem';
import ListItem from 'presentations/views/components/molecules/ResourceListItem';
import ResourcePageTemplate from 'presentations/views/components/templates/ResourcePageTemplate';
import {
  ResourceItem,
  ResourceListItem,
} from 'presentations/api/schema/resources';
import { useResourcePage } from './hooks';
import ResourceListSidebar from 'presentations/views/components/organisms/ResourceListSidebar';
import Icon from 'presentations/views/components/atoms/Icon';
import {
  modalImageAdId,
  showAdModal,
  showAdSquare,
  subImageAdId,
} from 'presentations/views/components/molecules/OwnedContentBanner';
import FooterContentBanner from 'presentations/views/components/molecules/OwnedContentBanner/FooterContentBanner';
import SpacemarketBanner from 'presentations/views/components/molecules/OwnedContentBanner/SpacemarketBanner';
import ResourceShareList from 'presentations/views/components/organisms/ResourceShareList';
import Modal from 'presentations/views/components/atoms/Modal';

import dynamic from 'next/dynamic';
import { blogCategory, blogSubCategories } from 'domains/models/blog_post';
import { useMemo } from 'react';
import { CustomerStoryListItem } from 'presentations/api/schema/contents';
import CustomerStoryMediaListItem from '../../molecules/CustomerStoryMediaListItem';

const Header = dynamic(() =>
  import('presentations/views/components/organisms/Header'),
);
const Breadcrumbs = dynamic(() =>
  import('presentations/views/components/organisms/Breadcrumbs'),
);

interface Props {
  resource: ResourceItem;
  populars: ResourceListItem[];
  customers: CustomerStoryListItem[];
  category: string;
}

export default function Component(props: Props) {
  const t = translateInComponent(translations);
  const { state, actions } = useResourcePage({
    category: props.category,
    resource: props.resource,
    populars: props.populars,
  });
  const isShowAd = [blogCategory, ...blogSubCategories].includes(
    props.category,
  );

  const campaign = useMemo(() => {
    if (!props.resource.tags || props.resource.tags.length === 0) {
      return;
    }
    const campaign = props.resource.tags.map((t) => t.slug).join(',');
    return {
      content: props.resource.slug,
      medium: 'magazine',
      campaign,
    };
  }, [props.resource.tags, props.resource.title]);

  const featureCustomer = useMemo(() => {
    return props.customers.slice(0, 1)[0] || null;
  }, [props.customers]);

  return (
    <ResourcePageTemplate
      breadcrumbs={<Breadcrumbs routes={state.routes} />}
      header={<Header viewer={state.viewer} />}
      campaign={campaign}
    >
      <>
        <Container>
          <MainContent>
            <ResourceContents
              resource={props.resource}
              productType={state.productType}
              customers={props.customers}
              isShowAd={!state.viewer || isShowAd}
            />
            <ShareContainer>
              <ShareTitle>{t('share')}</ShareTitle>
              <ShareIconContainer>
                <ResourceShareList
                  shareTitle={props.resource.title}
                  shareUrl={state.shareUrl}
                  direction="row"
                />
              </ShareIconContainer>
            </ShareContainer>
            {props.resource.relatedPosts.length > 0 && (
              <RecommendContainer>
                <RecommendTitle>{t('recommend')}</RecommendTitle>
                <RecommendList>
                  {props.resource.relatedPosts.map((item) => (
                    <RecommendListItemContainer key={item.id}>
                      <ListItem item={item} key={item.id} />
                    </RecommendListItemContainer>
                  ))}
                </RecommendList>
              </RecommendContainer>
            )}
            <SpacemarketAdContainer>
              <SpacemarketBanner />
            </SpacemarketAdContainer>
          </MainContent>
          {isShowAd && (
            <SubContent>
              <AdContainer>
                {showAdSquare({
                  adId: subImageAdId,
                  productType: state.productType,
                })}
              </AdContainer>
              {featureCustomer && (
                <CustomerList>
                  <CustomerStoryMediaListItem
                    customer={featureCustomer}
                    onClick={actions.onClickStory}
                  />
                </CustomerList>
              )}
              {props.populars.length > 0 && (
                <PopularContainer>
                  <PopularTitle>
                    <PopularIcon name="bulb-on-outline" />
                    {t('popular')}
                  </PopularTitle>
                  {props.populars.map((item) => (
                    <ListItemContainer key={item.id}>
                      <SubListItem item={item} />
                    </ListItemContainer>
                  ))}
                </PopularContainer>
              )}
              <MetaListContainer>
                <ResourceListSidebar />
              </MetaListContainer>
              <StickyAdContainer>
                {showAdSquare({
                  adId: subImageAdId,
                  productType: state.productType,
                })}
              </StickyAdContainer>
            </SubContent>
          )}
        </Container>
      </>
      {isShowAd && (
        <AdFooter>
          <FooterContentBanner productType={state.productType} isHideClose />
        </AdFooter>
      )}

      {!state.isDisableShowScreenBanner && (
        <Modal
          isOpen={state.isShowScreenBanner}
          onChange={actions.onCloseScreenBanner}
        >
          <AdModalInner>
            <AdClose onClick={actions.onCloseScreenBanner}>
              <AdCloseIcon name="close" />
            </AdClose>
            <AdListContainer>
              <AdWrapper>
                {showAdModal({
                  adId: modalImageAdId,
                  productType: state.productType,
                  position: 'sub_modal',
                })}
              </AdWrapper>
              {!state.isMobile && featureCustomer && (
                <AdCustomerStory>
                  <CustomerStoryMediaListItem
                    customer={featureCustomer}
                    onClick={actions.onClickStory}
                  />
                </AdCustomerStory>
              )}
            </AdListContainer>
          </AdModalInner>
        </Modal>
      )}
    </ResourcePageTemplate>
  );
}

const Container = styled.div`
  letter-spacing: 0.05em;
  display: flex;
  justify-content: center;
  padding: ${spacing(48)} ${spacing(24)} ${spacing(48)} 88px;
  ${mediaMax.desktop} {
    display: block;
  }
  ${mediaMax.tablet} {
    padding: 0;
  }
`;
const MainContent = styled.div`
  max-width: 900px;
  flex: 1;
  ${mediaMax.desktop} {
    margin: 0 auto;
  }
  ${mediaMax.tablet} {
    width: 100%;
    padding: 0 ${spacing(12)};
    margin: 0;
  }
`;
const MetaListContainer = styled.div`
  margin-top: ${spacing(40)};
`;
const SubContent = styled.div`
  /* width: 276px; */
  width: 300px;
  margin-left: ${spacing(40)};
  ${mediaMax.desktop} {
    width: 690px;
    margin: 0 auto;
    margin-top: ${spacing(24)};
  }
  ${mediaMax.tablet} {
    margin-left: 0;
    width: 100%;
    padding: 0 ${spacing(12)};
  }
`;

const AdContainer = styled.div`
  margin-bottom: ${spacing(24)};
`;
const StickyAdContainer = styled.div`
  display: none;
  ${mediaMin.desktop} {
    display: block;
    position: sticky;
    top: 96px;
  }
`;
const SpacemarketAdContainer = styled.div`
  margin-top: ${spacing(36)};
  margin-bottom: ${spacing(48)};
`;
const ShareContainer = styled.div`
  margin: 0 0 ${spacing(72)} 0;

  ${mediaMax.tablet} {
    margin: 0 0 ${spacing(40)} 0;
  }
`;

const ShareTitle = styled.div`
  margin: 0 0 ${spacing(20)} 0;

  font-weight: ${fontWeight('bold')};
  font-size: ${fontSize(20)};
  line-height: 150%;
  text-align: center;

  ${mediaMax.tablet} {
    margin: 0 0 ${spacing(24)} 0;
    font-size: ${fontSize(13)};
  }
`;

const ShareIconContainer = styled.div`
  display: flex;
  flex-flow: row;
  justify-content: center;
`;

const PopularContainer = styled.div``;

const PopularTitle = styled.div`
  border-bottom: 1px solid ${black};
  margin-bottom: ${spacing(24)};
  padding-bottom: ${spacing(16)};
  font-weight: ${fontWeight('bold')};
  font-size: ${fontSize(16)};
  display: flex;
  align-items: center;
`;

const RecommendContainer = styled.div`
  ${mediaMax.tablet} {
    margin-bottom: ${spacing(32)};
  }
`;
const RecommendList = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-column-gap: ${spacing(32)};
  border-bottom: 1px solid ${gray04};
  padding-bottom: ${spacing(32)};
  ${mediaMax.tablet} {
    display: block;
  }
`;

const RecommendTitle = styled.div`
  margin-bottom: ${spacing(32)};

  font-weight: ${fontWeight('bold')};
  font-size: ${fontSize(20)};
  line-height: 30px;

  ${mediaMax.tablet} {
    margin: 0 0 ${spacing(20)} 0;
    padding: 0 0 ${spacing(12)} 0;
    border-bottom: 1px solid ${gray01};
    font-size: ${fontSize(14)};
  }
`;

const RecommendListItemContainer = styled.div`
  padding-bottom: ${spacing(32)};
  margin-bottom: ${spacing(32)};
  :last-of-type {
    margin-bottom: 0;
    padding-bottom: 0;
    border-bottom: 0;
  }
  ${mediaMax.tablet} {
    margin-bottom: ${spacing(16)};
  }
`;

const ListItemContainer = styled.div`
  margin-bottom: ${spacing(16)};
  :last-of-type {
    margin-bottom: 0;
  }
`;
const PopularIcon = styled(Icon)`
  color: ${primary};
  margin-right: ${spacing(8)};
`;

export const AdFooter = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: ${zIndex(1100)};
`;

export const AdModalInner = styled.div`
  overflow: hidden;
  position: relative;

  ${mediaMax.tablet} {
    /* min-width: calc(100vw - 24px); */
  }
`;

export const AdListContainer = styled.div`
  display: flex;
`;
export const AdWrapper = styled.div`
  max-width: 300px;
  max-height: 300px;
  background-color: ${white};
  border-radius: ${borderRadius(12)};
`;
export const AdCustomerStory = styled.div`
  max-width: 300px;
  margin-left: ${spacing(20)};
`;
export const AdClose = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: ${spacing(8)};
`;
export const AdCloseIcon = styled(Icon)`
  color: ${white};
  width: 32px !important;
  height: 32px !important;
  cursor: pointer;
`;

const CustomerList = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  row-gap: ${spacing(24)};
  margin-bottom: ${spacing(40)};
  ${mediaMax.tablet} {
    display: none;
  }
`;
